import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { Skeleton } from "@mui/material";
import { ErrorIcon } from "Components/Icons/Arrows/ErrorIcon";
import { CheckIcon } from "Components/Icons/Arrows/CheckIcon";
import { EyeIcon } from "Components/Icons/Password/EyeIcon";
import { EyeClosedIcon } from "Components/Icons/Password/EyeClosedIcon";
import "./Input.scss";

/**
 * Componente Input con validación y estados visuales
 * @param {Object} props - Props del componente
 * @param {string} props.name - Nombre del input para formularios
 * @param {string} [props.value] - Valor actual del input
 * @param {Function} [props.onChange] - Función ejecutada al cambiar el valor
 * @param {string} [props.label] - Etiqueta del input
 * @param {string} [props.placeholder] - Texto placeholder
 * @param {string} [props.errorMessage] - Mensaje de error a mostrar
 * @param {'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date'} [props.type='text'] - Tipo de input HTML
 * @param {boolean} [props.disabled=false] - Estado deshabilitado del input
 * @param {boolean} [props.readOnly=false] - Estado de solo lectura (no permite escribir)
 * @param {boolean} [props.isValid] - Estado de validación exitosa
 * @param {boolean} [props.isInvalid] - Estado de validación fallida
 * @param {boolean} [props.isLoading=false] - Estado de carga que muestra skeleton
 * @param {string} [props.id] - ID único del input
 * @param {string} [props.className=''] - Clases CSS adicionales
 * @param {Object} [props.style={}] - Estilos inline adicionales
 * @param {React.ReactNode} [props.icon] - Icono personalizado a mostrar
 * @param {Object} [...props] - Props adicionales del elemento input
 * @returns {JSX.Element} Componente Input
 */
export const Input = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  errorMessage,
  type = "text",
  disabled = false,
  readOnly = false,
  isValid,
  isInvalid,
  isLoading = false,
  id,
  className = "",
  style = {},
  icon,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordType = type === "password";
  const isDateType = type === "date";
  const shouldShowValid = isValid && !disabled;
  const shouldShowInvalid = isInvalid && !disabled;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getIconToShow = () => {
    if (isDateType) return null;
    if (isPasswordType) {
      return (
        <div
          onClick={!disabled ? togglePasswordVisibility : undefined}
          className="password-toggle"
        >
          {showPassword ? <EyeIcon /> : <EyeClosedIcon />}
        </div>
      );
    }
    if (shouldShowInvalid) return <ErrorIcon />;
    if (shouldShowValid) return <CheckIcon />;

    if ((disabled || (!isValid && !isInvalid)) && icon) return icon;
    return null;
  };

  const getIconClass = () => {
    if (isPasswordType) return "password-icon";
    if (shouldShowInvalid) return "error-icon";
    if (shouldShowValid) return "success-icon";
    return "default-icon";
  };

  const iconToShow = getIconToShow();
  const hasIcon = !!iconToShow;

  const inputClasses = [
    "input-custom",
    hasIcon && "has-icon",
    isDateType && "input-date",
    isPasswordType && "input-password",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const labelClasses = [
    "label-custom",
    shouldShowInvalid && "error-label",
    shouldShowValid && "success-label",
  ]
    .filter(Boolean)
    .join(" ");

  const inputType = isPasswordType && showPassword ? "text" : type;

  if (isLoading) {
    return (
      <Form.Group style={style}>
        {label && (
          <Skeleton
            variant="text"
            width="40%"
            height={16}
            sx={{ marginBottom: "8px", marginLeft: "16px" }}
          />
        )}
        <Skeleton
          variant="rounded"
          width="100%"
          height={48}
          sx={{ borderRadius: "8px" }}
        />
      </Form.Group>
    );
  }

  return (
    <Form.Group style={style}>
      {label && (
        <Form.Label htmlFor={id} className={labelClasses}>
          {label}
        </Form.Label>
      )}

      <div className="input-wrapper">
        <Form.Control
          id={id}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          isValid={shouldShowValid}
          isInvalid={shouldShowInvalid}
          className={inputClasses}
          {...props}
        />

        {iconToShow && (
          <div className={`input-icon ${getIconClass()}`}>{iconToShow}</div>
        )}
      </div>

      {shouldShowInvalid && errorMessage && (
        <div className="error-input">{errorMessage}</div>
      )}
    </Form.Group>
  );
};
